import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { USER_TYPE } from '../../../../util/enums';
import Icons from '../../../../components/Icons/Icons';
import CartLink from '../CartLink/CartLink';
import { getUserTypePreference } from '../../../../util/dataExtractor';
import { ensureCurrentUser } from '../../../../util/data';
import MegaMenu from './components/MegaMenu/MegaMenu';
import { getUserType } from '../../../../util/genericHelpers';
import { canManageShopNewBrands } from '../../../../util/shopNewBrands';

const SignupLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <Icons name="user" />
      Sign in / Register
    </NamedLink>
  );
};

const NewListingPageLink = () => {
  return (
    <NamedLink name="NewListingPage" className={css.topbarLink}>
      <FormattedMessage id="ManageListingsPage.createListing" />
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <FormattedMessage id="TopbarDesktop.login" />
    </NamedLink>
  );
};

const FavoriteLink = () => {
  return (
    <NamedLink name="LandingPage" className={css.topbarLink}>
      <Icons name="unfavorite" />
    </NamedLink>
  );
};
const ExchangeLink = () => {
  return (
    <NamedLink name="LandingPage" className={css.topbarLink}>
      <Icons name="exchange" />
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <FormattedMessage id="TopbarDesktop.inbox" />
      {notificationDot}
    </NamedLink>
  );
};

const manageListingsMenuItem = currentPageClass => (
  <MenuItem key="ManageListingsPage">
    <NamedLink
      className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <span className={css.menuItemBorder} />
      <FormattedMessage id="TopbarDesktop.yourListingsLink" />
    </NamedLink>
  </MenuItem>
);

const shopNewBrandsMenuItem = currentPageClass => (
  <MenuItem key="ShopNewBrandsPage">
    <NamedLink
      className={classNames(css.menuLink, currentPageClass('ShopNewBrandsPage'))}
      name="ShopNewBrandsPage"
    >
      <span className={css.menuItemBorder} />
      <FormattedMessage id="TopbarDesktop.shopNewBrandsLink" />
    </NamedLink>
  </MenuItem>
);

const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };
  const userType = getUserType(currentUser);
  const user = ensureCurrentUser(currentUser);
  const userTypePreference = getUserTypePreference(user);
  const shopNewBrandsMenuItemMaybe = canManageShopNewBrands(currentUser)
    ? shopNewBrandsMenuItem(currentPageClass)
    : null;
  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      {userType === USER_TYPE.SELLER ? (
        <MenuContent className={css.profileMenuContent}>
          {manageListingsMenuItem(currentPageClass)}
          {shopNewBrandsMenuItemMaybe}

          <MenuItem
            key={
              USER_TYPE === getUserTypePreference.SELLER
                ? 'ShopSettingsPage'
                : 'ProfileSettingsPage'
            }
          >
            <NamedLink
              className={classNames(
                css.menuLink,
                currentPageClass(
                  userTypePreference === USER_TYPE.SELLER
                    ? 'ShopSettingsPage'
                    : 'ProfileSettingsPage'
                )
              )}
              name={
                userTypePreference === USER_TYPE.SELLER ? 'ShopSettingsPage' : 'ProfileSettingsPage'
              }
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage
                id={
                  userTypePreference === USER_TYPE.SELLER
                    ? 'TopbarDesktop.shopSettingsLink'
                    : 'TopbarDesktop.profileSettingsLink'
                }
              />
            </NamedLink>
          </MenuItem>

          <MenuItem key="FavoritesPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('FavoritesPage'))}
              name="FavoritesPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.favoritePageLink" />
            </NamedLink>
          </MenuItem>

          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>

          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      ) : (
        <MenuContent className={css.profileMenuContent}>
          {manageListingsMenuItem(currentPageClass)}
          {shopNewBrandsMenuItemMaybe}

          <MenuItem
            key={userType === USER_TYPE.BUYER ? 'ProfileSettingsPage' : 'ProfileSettingsPage'}
          >
            <NamedLink
              className={classNames(
                css.menuLink,
                currentPageClass(
                  userType === USER_TYPE.BUYER ? 'ProfileSettingsPage' : 'ProfileSettingsPage'
                )
              )}
              name={userType === USER_TYPE.BUYER ? 'ProfileSettingsPage' : 'ProfileSettingsPage'}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>

          <MenuItem key="FavoritesPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('FavoritesPage'))}
              name="FavoritesPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.favoritePageLink" />
            </NamedLink>
          </MenuItem>

          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>

          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      )}
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {boolean} props.currentUserHasListings
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    viewport,
  } = props;
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setOpen(false); // close when clicked outside
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;
  const userType = getUserType(currentUser);

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const newListingPageLinkMaybe =
    isAuthenticatedOrJustHydrated && userType === USER_TYPE.SELLER ? <NewListingPageLink /> : null;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;
  const favoriteLinkMaybe = isAuthenticatedOrJustHydrated ? null : <FavoriteLink />;
  const exchangeLinkMaybe = isAuthenticatedOrJustHydrated ? null : <ExchangeLink />;
  const cartLinkMaybe = isAuthenticatedOrJustHydrated ? null : <CartLink />;
  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav className={classes}>
      {/* <div className={css.subHeader}>
        <div className={css.contentWidth}>
          <p>COMPLIMENTARY SHIPPING ON ALL ORDERS OVER $300</p>
        </div>
      </div> */}
      <div className={css.mainHeader}>
        <div className={css.contentWidth}>
          <div className={css.leftNav}>
            <LinkedLogo
              className={css.logoLink}
              layout="desktop"
              alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
            {/* <NamedLink className={css.topbarLink} name="LandingPage">
              <FormattedMessage id="TopbarDesktop.home" />
            </NamedLink>
            <NamedLink className={css.topbarLink} name="OurAffiliatesPage">
              <FormattedMessage id="OurAffiliatesPage.title" />
            </NamedLink> */}
            <MegaMenu viewport={viewport} />
          </div>
          <div className={css.rightNav}>
            <CustomLinksMenu
              currentPage={currentPage}
              customLinks={customLinks}
              intl={intl}
              hasClientSideContentReady={
                authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
              }
            />
            {!open && (
              <button className={css.searchIcon} onClick={() => setOpen(!open)}>
                <Icons name="search" />
              </button>
            )}
            {open && <div ref={searchRef}>{searchFormMaybe}</div>}

            {/* {loginLinkMaybe} */}
            <CartLink />
            {newListingPageLinkMaybe}
            {favoriteLinkMaybe}
            {exchangeLinkMaybe}
            {/* {cartLinkMaybe} */}
            {inboxLinkMaybe}
            {signupLinkMaybe}
            {profileMenuMaybe}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
